/**
 * @file useCreateReservation
 *
 * Creates a request w/o pricing (Reservation)
 */

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import pick from "lodash/pick";
import { useHistory } from "react-router-dom";
import first from "lodash/first";
import map from "lodash/map";
import isNull from "lodash/isNull";
import { UseFormSetError } from "react-hook-form";
import { formatPhoneNumber } from "utils/phoneNumberFormatter/phoneNumberFormatter";
import {
  CREATE_CONTACT_MUTATION,
  CREATE_RESERVATION_MUTATION,
  CREATE_WALLET_MUTATION,
  ROLLBACK_CREATE_CONTACT_MUTATION,
  UPDATE_CONTACT_MUTATION,
} from "../../../globals/graphql";
import {
  useAnalytics,
  useAuth,
  useCurrentUser,
  useDefaultPassenger,
  useGoogleTracking,
  useLaunchDarklyFlags,
  useOperator,
  usePocketflowsTracking,
  useQueryParams,
  useSnackbar,
} from "../../../globals/hooks";
import {
  getErrorCode,
  getErrorMessage,
} from "../../../moovsErrors/getErrorMessage";
import { ContactInformationInput, PricingLayout } from "../../../types";
import { fromGlobalId } from "../../../utils/auth/helpers";
import { useCreateRequestContext } from "../context/useCreateRequestContext";
import { sanitizeRequestForCreateMutation } from "components/confirm/hooks/helpers";
import { calculateCreateTripPricing } from "globals/hooks/useAutomatedPricing";
import { CreateReservationFormState } from "../steps/confirm/components/CreateReservationForm/form/schemaValidation";
import { mergeConfirmRequestFormState } from "../context/helpers/mergeConfirmRequestFormState";
import { useTermsAndConditions } from ".";
import useIntroTermsAndConditions from "pages/new/hooks/useIntroTermsAndConditions";
import { deriveStrictestCancellationPolicy } from "../utils/deriveStrictestCancellationPolicy";
import { useCancellationPolicies } from "components/confirm/form/components/TermsAndConditionsBlock/hooks/useCancellationPolicies";
import { usePricelessBookingContext } from "../steps/confirm/context";
import { getIsNetflixLogin } from "../../../utils/auth/isNetflixLogin";
import getDataForUpdateContact from "../utils/getDataForUpdateContact";
import type { ReservationPaymentSetupAdapter } from "./useReservationPaymentSetup";

enum StripeErrorCode {
  EMAIL_INVALID = "email_invalid",
  INVALID_NUMBER = "invalid_number",
  INCORRECT_NUMBER = "incorrect_number",
  INVALID_EXPIRY_MONTH = "invalid_expiry_month",
  INVALID_EXPIRY_YEAR = "invalid_expiry_year",
  INVALID_CVC = "invalid_cvc",
  INCORRECT_CVC = "incorrect_cvc",
  CARD_DECLINED = "card_declined",
  EXPIRED_CARD = "expired_card",
  INCORRECT_ADDRESS = "incorrect_address",
  INCORRECT_ZIP = "incorrect_zip",
  PROCESSING_ERROR = "processing_error",
  AUTHENTICATION_REQUIRED = "authentication_required",
  INSUFFICIENT_FUNDS = "insufficient_funds",
  CARD_DECLINE_RATE_LIMIT_EXCEEDED = "card_decline_rate_limit_exceeded",
}

const STRIPE_ERROR_MESSAGES = {
  [StripeErrorCode.EMAIL_INVALID]: {
    fieldError: "bookingContact.email",
    message: "Please enter a valid email address",
  },
  [StripeErrorCode.INVALID_NUMBER]: {
    fieldError: "paymentMethod.creditCardError",
    message: "The card number is incorrect. Please check and try again.",
  },
  [StripeErrorCode.INCORRECT_NUMBER]: {
    fieldError: "paymentMethod.creditCardError",
    message: "The card number is incorrect. Please check and try again.",
  },
  [StripeErrorCode.INVALID_EXPIRY_MONTH]: {
    fieldError: "paymentMethod.creditCardError",
    message:
      "The card's expiration date is incorrect. Please check and try again.",
  },
  [StripeErrorCode.INVALID_EXPIRY_YEAR]: {
    fieldError: "paymentMethod.creditCardError",
    message:
      "The card's expiration date is incorrect. Please check and try again.",
  },
  [StripeErrorCode.INVALID_CVC]: {
    fieldError: "paymentMethod.creditCardError",
    message: "The card's CVC code is incorrect. Please check and try again.",
  },
  [StripeErrorCode.INCORRECT_CVC]: {
    fieldError: "paymentMethod.creditCardError",
    message: "The card's CVC code is incorrect. Please check and try again.",
  },
  [StripeErrorCode.CARD_DECLINED]: {
    fieldError: "paymentMethod.creditCardError",
    message: "Your card was declined. Please try a different payment method.",
  },
  [StripeErrorCode.EXPIRED_CARD]: {
    fieldError: "paymentMethod.creditCardError",
    message: "Your card has expired. Please try a different card.",
  },
  [StripeErrorCode.INCORRECT_ADDRESS]: {
    fieldError: "paymentMethod.creditCardError",
    message:
      "The billing address information is incorrect. Please check and try again.",
  },
  [StripeErrorCode.INCORRECT_ZIP]: {
    fieldError: "paymentMethod.creditCardError",
    message:
      "The billing address information is incorrect. Please check and try again.",
  },
  [StripeErrorCode.PROCESSING_ERROR]: {
    fieldError: "paymentMethod.creditCardError",
    message:
      "An error occurred while processing your card. Please try again later.",
  },
  [StripeErrorCode.AUTHENTICATION_REQUIRED]: {
    fieldError: "paymentMethod.creditCardError",
    message: "This card requires authentication. Please try a different card.",
  },
  [StripeErrorCode.INSUFFICIENT_FUNDS]: {
    fieldError: "paymentMethod.creditCardError",
    message:
      "Your card has insufficient funds. Please try a different payment method.",
  },
  [StripeErrorCode.CARD_DECLINE_RATE_LIMIT_EXCEEDED]: {
    fieldError: "paymentMethod.creditCardError",
    message:
      "This card has been declined too many times. Please try a different card.",
  },
};

type UseCreateReservationParams = {
  onVerificationConsumed?: () => void;
  setError: UseFormSetError<any>;
  createReservationFormState: CreateReservationFormState;
  reservationPaymentSetup?: ReservationPaymentSetupAdapter;
};

function useCreateReservation(params: UseCreateReservationParams) {
  const {
    setError,
    createReservationFormState,
    onVerificationConsumed,
    reservationPaymentSetup,
  } = params;

  // contexts
  const [request] = useCreateRequestContext();
  const { isPricelessBooking } = usePricelessBookingContext();

  // hooks
  const { authStage, loginData } = useAuth();
  const currentUser = useCurrentUser();
  const snackbar = useSnackbar();
  const history = useHistory();
  const { operator } = useOperator();
  const stripe = useStripe();
  const stripeElements = useElements();
  const {
    operator: { pricingLayout, settings: { tripStatusUpdateTextTo } = {} },
  } = useOperator();
  const { track } = useAnalytics();
  const { googleTrack, getGclid } = useGoogleTracking();
  const queryParams = useQueryParams();
  const termsData = useTermsAndConditions();
  const { cancellationPolicies: cancellationPoliciesData } =
    useCancellationPolicies({ trips: null });
  const intro = useIntroTermsAndConditions();
  const { enableLinkedPassenger } = useLaunchDarklyFlags();
  const { selectedPassenger } = useDefaultPassenger() || {};
  const { identifyUser } = usePocketflowsTracking();
  const isNetflixLogin = getIsNetflixLogin();

  // refs
  // used to make sure data passed to createReservation when a callback
  // is updated (otherwise is a closure). This also requires a
  // setTimeout to wait for next render
  const currentUserRef = useRef(currentUser);
  const paymentElementSubmissionRef = useRef(false);
  const submittingRef = useRef(false);
  const activeRef = useRef(true);
  const isAuthenticated = authStage === "authenticated";

  // effects
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // state
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [newContactId, setNewContactId] = useState("");

  // mutations
  const [createContact] = useMutation(CREATE_CONTACT_MUTATION, {
    onError(error) {
      setIsCreatingReservation(false);
      const errorMessage = getErrorMessage(error) || "Error creating contact.";

      snackbar.error(errorMessage);
      console.log(error);
    },
  });

  const [rollbackCreateContact] = useMutation(
    ROLLBACK_CREATE_CONTACT_MUTATION,
    {
      onError(error) {
        setIsCreatingReservation(false);
        const errorMessage =
          getErrorMessage(error) || "Error rolling back contact.";

        snackbar.error(errorMessage);
        console.log(error);
      },
    }
  );

  const [createWallet] = useMutation(CREATE_WALLET_MUTATION, {
    onError(error) {
      setIsCreatingReservation(false);
      const errorMessage = getErrorMessage(error) || "Error creating payment.";

      snackbar.error(errorMessage);
      console.log(error);
    },
  });

  const [createReservationMutation] = useMutation(CREATE_RESERVATION_MUTATION, {
    onCompleted(data) {
      const { autoPaymentType, promoCodeCustomerInput } =
        createReservationFormState;

      const trackingChargeType = !autoPaymentType
        ? "no charge"
        : autoPaymentType === "partial"
          ? "partial charge"
          : "full charge";

      const hasChildSeatApplied = [
        "rearFacingSeatAmt",
        "boosterSeatAmt",
        "forwardFacingSeatAmt",
      ].some(
        (childSeatField) =>
          !isNull(
            data.createReservation.request.trips[0].routes[0].pricing[
              childSeatField
            ]
          )
      );

      // linked passenger tracking
      let linkedPassengerCount = 0;
      let temporaryPassengerCount = 0;
      let bookingContactCount = 0;

      data.createReservation.request.trips.forEach((trip) => {
        if (trip.tempPassenger?.name) {
          temporaryPassengerCount++;
        } else if (
          trip.contact.id === data.createReservation.request.bookingContact.id
        ) {
          bookingContactCount++;
        } else {
          linkedPassengerCount++;
        }
      });

      track(
        isPricelessBooking
          ? "reservationRequest_newReceived"
          : "reservations_newReceived",
        {
          type: isPricelessBooking ? "reservationRequest" : "BRA",
          charge_type: trackingChargeType,
          ...(enableLinkedPassenger && {
            passengerType: {
              bookingContact: bookingContactCount,
              temporaryPassenger: temporaryPassengerCount,
              linkedPassengerCount: linkedPassengerCount,
            },
          }),
          ...(promoCodeCustomerInput && { promo: "code_applied" }),
          ...(hasChildSeatApplied && { child_seat: "seat_added" }),
        }
      );

      const operatorId = fromGlobalId(operator.id).id;

      // set analytics to track user if not authenticated
      // if authenticated this already happened
      if (!isAuthenticated) {
        const bookingContact = data.createReservation.request.bookingContact;
        const formattedTwilioNumber = formatPhoneNumber(
          operator.twilioPhoneNumber?.phoneNumber
        )?.formatted;

        window.analytics.identify(fromGlobalId(bookingContact.id).id, {
          operator_id: operatorId,
          email: bookingContact.email,
          name: `${bookingContact.firstName} ${bookingContact.lastName}`,
          source: "customer",
        });

        window.analytics.group(operatorId, {
          operator_id: operatorId,
          name: operator.name,
          phone: formattedTwilioNumber || operator.voicePhoneNumber,
          email: operator.generalEmail,
          location: operator.address,
          source: "customer",
          plan: operator.plan,
        });
      }

      googleTrack(
        "moovs_create_reservation",
        data.createReservation.request.totalAmount
      );

      const { email } = data.createReservation.request.bookingContact;
      identifyUser(operatorId, email);

      setIsCreatingReservation(false);
      submittingRef.current = false;
      onVerificationConsumed?.();

      history.push({
        pathname: `/${operator.nameSlug}/order/${data.createReservation.request.id}`,
        search: "?successDialog=true",
        state: { autoPaymentType },
      });
    },
    onError(error) {
      setIsCreatingReservation(false);
      onVerificationConsumed?.();
      submittingRef.current = false;
      const errorCode = getErrorCode(error);
      if (errorCode === "MOOVS_INVALID_EMAIL") {
        setError("bookingContact.email", {
          message: "Please enter valid email address",
        });
      }

      let errorMessage = "";
      if (errorCode === "MOOVS_DAILY_PAYMENT_LIMIT_REACHED") {
        errorMessage = `We're unable to process your payment at this time. ${
          operator.generalEmail
            ? `Please reach out to ${operator.generalEmail} for assistance.`
            : ""
        }`;
      } else {
        errorMessage = getErrorMessage(error) || "Error creating reservation.";
      }

      snackbar.error(errorMessage);

      // only run if server error and new contact was created
      if (
        !paymentElementSubmissionRef.current &&
        newContactId &&
        error.networkError
      ) {
        rollbackCreateContact({
          variables: {
            input: {
              contactId: newContactId,
            },
          },
        });
      }
    },
  });

  // creates stripe wallet and card. If new contact,
  // also creates a contact so it can be linked
  // to the stripe customer. If card error, will
  // rollback creating new contact.
  const createStripeWalletCardAndContact = async (
    cardholderName: string,
    contact: ContactInformationInput
  ) => {
    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = stripeElements.getElement(CardElement);

    const shouldCreateNewContact = !contact.id;
    let contactId = contact.id;

    // create contact if doesn't exist
    if (shouldCreateNewContact) {
      const inputContact = pick(contact, [
        "firstName",
        "lastName",
        "email",
        "mobilePhone",
        "phoneCountryCode",
        "phoneCountryDialCode",
        "phoneCountryName",
        "phoneCountryFormat",
      ]);

      const { data } = await createContact({
        variables: {
          input: inputContact,
        },
      });

      // set new contact id to delete later in event of error
      setNewContactId(data.createContact.contact.id);
      contactId = data.createContact.contact.id;
    }

    const { data } = await createWallet({
      variables: {
        input: {
          contactId,
        },
      },
    });

    if (!data) {
      return;
    }

    try {
      const confirmCardSetupResult = await stripe.confirmCardSetup(
        data.createWallet.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: cardholderName,
              email: contact.email?.trim() || undefined,
            },
          },
        }
      );

      if (confirmCardSetupResult.error) {
        const errorCode = confirmCardSetupResult.error.code;
        console.error("Stripe error:", confirmCardSetupResult.error);

        // Handle specific error codes
        // Stripe error codes can be found here: https://docs.stripe.com/error-codes
        const errorInfo = STRIPE_ERROR_MESSAGES[errorCode as StripeErrorCode];
        if (errorInfo) {
          setError(errorInfo.fieldError, {
            message: errorInfo.message,
          });
          snackbar.error(errorInfo.message);
        } else {
          const fallbackErrorMessage =
            confirmCardSetupResult.error.message ||
            "Unable to process payment with the card information provided. Please ensure the card information and billing address are correct.";
          setError("paymentMethod.creditCardError", {
            message: fallbackErrorMessage,
          });
          snackbar.error(fallbackErrorMessage);
        }

        return;
      }

      return {
        contact: { ...contact, id: contactId },
        setupIntent: confirmCardSetupResult.setupIntent,
      };
    } catch (error) {
      // rollback creating new contact
      if (shouldCreateNewContact) {
        await rollbackCreateContact({
          variables: {
            input: {
              contactId,
            },
          },
        });
      }

      console.error("Unexpected error during card setup:", error);
      snackbar.error("An unexpected error occurred. Please try again later.");
    }
  };

  const [updateContact] = useMutation(UPDATE_CONTACT_MUTATION, {
    onCompleted: () => {
      snackbar.success("Successfully updated contact!");
    },
    onError: (error) => {
      console.error(error);
      snackbar.error(error.message || "Error updating passenger");
    },
  });

  const createReservation = async (
    createReservationFormState: CreateReservationFormState,
    turnstileToken: string
  ) => {
    if (submittingRef.current) return;
    const usePaymentElement =
      reservationPaymentSetup?.enabled && operator.enableCreditCardWhenBooking;
    paymentElementSubmissionRef.current = !!usePaymentElement;
    const authenticatedCustomerWithCreditCardOnFile =
      isAuthenticated && !!currentUserRef?.current?.paymentMethods?.length;

    if (
      operator.enableCreditCardWhenBooking &&
      !usePaymentElement &&
      !authenticatedCustomerWithCreditCardOnFile &&
      (!stripe || !stripeElements)
    ) {
      // if stripe elements fail to load, prevent form submission.
      snackbar.error("error loading payment elements");
    }

    setIsCreatingReservation(true);
    submittingRef.current = !!usePaymentElement;

    const updatedRequest = mergeConfirmRequestFormState(
      request,
      createReservationFormState
    );

    let verifiedContact = currentUserRef.current;

    if (
      isNetflixLogin &&
      createReservationFormState.bookingContact &&
      loginData
    ) {
      const dataForResponse = getDataForUpdateContact(
        createReservationFormState,
        loginData
      );
      const updatedContact = await updateContact(dataForResponse);

      verifiedContact.mobilePhone =
        updatedContact.data.updateContact.contact.mobilePhone;

      if (enableLinkedPassenger) {
        selectedPassenger.mobilePhone =
          updatedContact.data.updateContact.contact.mobilePhone;
      }
    }

    let paymentMethodId: string;
    let paymentMethodClientSecret: string;
    if (usePaymentElement) {
      try {
        const method = await reservationPaymentSetup.save(
          createReservationFormState
        );
        paymentMethodId = method.id;
        // Signed-in payers authorize owned cards through their session. A newly
        // created setup secret may not belong to an existing saved card.
        paymentMethodClientSecret = isAuthenticated ? undefined : method.clientSecret;
      } catch (error) {
        if (!activeRef.current) return;
        submittingRef.current = false;
        setIsCreatingReservation(false);
        snackbar.error(
          getErrorMessage(error) ||
            (error instanceof Error
              ? error.message
              : "Unable to save card. Please try again.")
        );
        return;
      }
    }

    const createRequestInput = sanitizeRequestForCreateMutation({
      request: updatedRequest,
      pricingLayout: operator.settings?.pricingLayout,
      verifiedContact: isAuthenticated ? verifiedContact : undefined,
      preparedGuestContact:
        usePaymentElement && !isAuthenticated
          ? reservationPaymentSetup.stateRef.current?.guest?.contact
          : undefined,
      pickUpVariantSelected: createReservationFormState.pickUpVariantSelected,
      enableLinkedPassenger,
      selectedPassenger,
      tripStatusUpdateTextTo,
    });

    // fix base rate amt for priceless booking
    if (
      isPricelessBooking &&
      !createRequestInput.trips[0].routes[0].baseRateAmt
    ) {
      createRequestInput.trips[0].routes[0].baseRateAmt = null;
      if (
        createRequestInput.trips[0].returnTrip &&
        !createRequestInput.trips[0].returnTrip.routes[0].baseRateAmt
      ) {
        createRequestInput.trips[0].returnTrip.routes[0].baseRateAmt = null;
      }
    }

    let stripePaymentMethodId;
    let setupIntent;
    let newContact;

    // create stripe wallet if user has not selected a credit card
    if (
      operator.enableCreditCardWhenBooking &&
      !usePaymentElement &&
      !authenticatedCustomerWithCreditCardOnFile
    ) {
      const createStripeWalletResponse = await createStripeWalletCardAndContact(
        createReservationFormState.paymentMethod.cardholderName,
        currentUserRef.current || {
          id: null,
          ...updatedRequest.bookingContact,
        }
      );

      setupIntent = createStripeWalletResponse?.setupIntent;
      newContact = createStripeWalletResponse?.contact;
      stripePaymentMethodId = setupIntent?.payment_method;

      if (!stripePaymentMethodId) {
        setIsCreatingReservation(false);
        return;
      }

      // use newly created contact
      createRequestInput.trips[0].contact = pick(newContact, [
        "id",
        "firstName",
        "lastName",
        "email",
        "mobilePhone",
      ]);
    }

    // create request only if new card went through,
    // or when its a verified contact with an existing card,
    // or the operator has disabled credit cards when booking.
    if (
      !stripePaymentMethodId &&
      !paymentMethodId &&
      !authenticatedCustomerWithCreditCardOnFile &&
      operator.enableCreditCardWhenBooking
    ) {
      setIsCreatingReservation(false);
      snackbar.error("Error creating reservation");
      return;
    }

    // if authenticated and using an existing card, set stripePaymentMethodId equal to that card
    if (
      operator.enableCreditCardWhenBooking &&
      !usePaymentElement &&
      authenticatedCustomerWithCreditCardOnFile
    ) {
      stripePaymentMethodId = createReservationFormState.stripePaymentMethodId;
    }

    const { driverGratuity, returnDriverGratuity, cashGratuity } =
      getDriverGratuity(
        request.trip,
        request.returnTrip,
        pricingLayout,
        createReservationFormState.driverGratuityPctCustomerInput,
        createReservationFormState.pickUpVariantSelected,
        createReservationFormState.promoCodeCustomerInput,
        createReservationFormState.additonalItemsByTrip?.[0]
      );

    const baseRateAutomation =
      request.trip.routes[0].vehicle.baseRateAutomation;

    const printedName = createReservationFormState.printedName?.trim();

    // format terms & conditions and cancellation policy
    const terms = termsData.map(({ name, description }) => {
      return { name, description };
    });

    const selectedVehicleIds = request.trip.routes.map(
      (route) => route.vehicle.id
    );

    const cancellationPolicies = deriveStrictestCancellationPolicy({
      cancellationPoliciesData,
      selectedVehicleIds,
    });

    createReservationMutation({
      variables: {
        input: {
          ...createRequestInput,
          paymentMethodId,
          stripePaymentMethodId,
          clientSecret: paymentMethodClientSecret || setupIntent?.id,
          checkoutContext: usePaymentElement
            ? reservationPaymentSetup.stateRef.current?.checkoutContext
            : undefined,
          gclid: getGclid(),
          queryString: queryParams.toString(),
          turnstileToken,
          ...(baseRateAutomation && {
            baseRateAutomation: {
              total: baseRateAutomation.total,
              lineItems: map(
                baseRateAutomation.lineItems,
                ({ __typename, ...lineItem }) => lineItem
              ),
            },
          }),
          // user entered driver gratuity. should only
          // have a value if no driver gratuity was
          // included in pricing layout
          ...(operator.gratuityWhenBooking && {
            driverGratuity,
            returnDriverGratuity,
            cashGratuity,
          }),
          autoPaymentAmt: createReservationFormState.autoPaymentAmt,
          printedName,
          intro,
          terms,
          cancellationPolicies,
          isUnconfirmedRequest: isPricelessBooking,
        },
      },
    }).catch(() => {});
  };

  return {
    createReservation,
    isCreatingReservation,
  };
}

export { useCreateReservation };

const getDriverGratuity = (
  trip: {
    stops?: any[];
    routes: {
      vehicle?: {
        id: string;
        baseRateAutomation?: {
          total: number;
        };
        returnBaseRateAutomation?: {
          total: number;
        };
      };
    }[];
  },
  returnTrip:
    | {
        stops?: any[];
      }
    | null
    | undefined,
  pricingLayout: PricingLayout,
  driverGratuityPctCustomerInput: number | "cash",
  pickUpVariantSelected?: any,
  userAppliedPromoCode?: any,
  additonalItemsForOutboundTrip?: any
) => {
  const cashGratuity = driverGratuityPctCustomerInput === "cash";

  if (cashGratuity) {
    return {
      cashGratuity,
      driverGratuity: 0,
      returnDriverGratuity: 0,
    };
  }

  const { baseRateAutomation, returnBaseRateAutomation } = first(
    trip.routes
  ).vehicle;

  const automatedBaseRate = baseRateAutomation?.total || null;
  const returnAutomatedBaseRate = returnBaseRateAutomation?.total || null;

  const { tripPricing } = calculateCreateTripPricing({
    baseRateAmt: automatedBaseRate || 0,
    pricingLayout,
    driverGratuityPctCustomerInput: driverGratuityPctCustomerInput as number,
    pickUpVariantSelected,
    isAirportPickUpStop: isAirportPickUpStop(trip),
    userAppliedPromoCode,
    childSeatInput: additonalItemsForOutboundTrip?.childSeats,
  });

  const returnDriverGratuity = returnAutomatedBaseRate
    ? calculateCreateTripPricing({
        baseRateAmt: returnAutomatedBaseRate,
        pricingLayout,
        driverGratuityPctCustomerInput:
          driverGratuityPctCustomerInput as number,
        pickUpVariantSelected,
        isAirportPickUpStop: isAirportPickUpStop(returnTrip),
        userAppliedPromoCode,
        childSeatInput: additonalItemsForOutboundTrip?.childSeats,
      }).tripPricing.driverGratuity.value
    : 0;

  return {
    driverGratuity: tripPricing.driverGratuity.value,
    returnDriverGratuity,
    cashGratuity: false,
  };
};

const isAirportPickUpStop = (trip?: { stops?: any[] } | null) => {
  const pickUpStop = trip?.stops?.[0];

  return (
    !!pickUpStop?.trackedFlight ||
    !!pickUpStop?.pickUpGooglePlaceTypes?.includes("airport") ||
    !!pickUpStop?.airport?.icaoCode
  );
};
